import { setupFilterMultiSelects } from './schedule-five-overview-filters';

export const isIndividualScheduleFive = (item) => item?.scheduleFiveType === 'individual';

const _actualWidth = 120;
const _maxRowHeight = 100;

export const buildNoRecordsConfig = suppress => (suppress ? { noRecords: { template: '<span></span>' } } : {});

export const renderTagList = (commaSeparated, cssClass, { dedupe = false } = {}) => {
    let values = commaSeparated.split(',');
    if (dedupe) values = values.filter((v, i, a) => a.indexOf(v) === i);
    const output = values.map(v => `<div class="tag ${cssClass} tag-small">${v}</div>`).join('');
    return addMaxHeight(output);
};

export const renderStatusBadge = status => {
    const map = {
        '0': { text: 'Not Started', cssClass: 'js-red' },
        '1': { text: 'In Progress', cssClass: 'js-yellow' },
        '2': { text: 'Ready for Review', cssClass: 'js-green' },
    };
    const { text, cssClass } = map[status] ?? map['0'];
    return `<div class="${cssClass}"><strong>${text}</strong></div>`;
};

// Variance column. The server supplies remainingVariance (signed dollars, the total
// unspread variance across all nine Schedule 5 cost types) and isVarianceWithinTolerance
// (a SQL bit: true when every cost type is inside the +/- $100 allowance, false when any
// single one exceeds it). The green/red tag classes and the wording are the same ones the
// Schedule 5 detail page uses for this state, so the two surfaces read alike.
const _varianceTagClasses = { inTolerance: 'tag--green', outOfTolerance: 'tag--red' };
const _varianceTitles = { inTolerance: 'No Variance', outOfTolerance: 'Variance' };

// Accept 1 / '1' / 'true' as well as a real boolean: the grid row is an untyped
// Dictionary<string, object> server-side, so the bit's JSON form is not guaranteed.
export const isWithinTolerance = flag =>
    flag === true || flag === 1 || flag === '1' || String(flag).toLowerCase() === 'true';

export const getVarianceTagClass = flag =>
    isWithinTolerance(flag) ? _varianceTagClasses.inTolerance : _varianceTagClasses.outOfTolerance;

export const getVarianceTagTitle = flag =>
    isWithinTolerance(flag) ? _varianceTitles.inTolerance : _varianceTitles.outOfTolerance;

export const formatVarianceAmount = value => kendo.toString(Number(value) || 0, 'c2');

export const buildVarianceTag = (formattedAmount, flag) =>
    `<div class="tag ${getVarianceTagClass(flag)}" title="${getVarianceTagTitle(flag)}">${formattedAmount}</div>`;

export const renderVarianceTag = dataItem =>
    buildVarianceTag(formatVarianceAmount(dataItem?.remainingVariance), dataItem?.isVarianceWithinTolerance);

export const getStartDateForData = data => (data.length ? findEarliestMonthKey(data) : new Date());

export const applyStatusRowColors = () => {
    $('div.js-green').parent().addClass('bg-green-light');
    $('div.js-yellow').parent().addClass('bg-yellow-light');
    $('div.js-red').parent().addClass('bg-danger-light');
};

let _company;
let dateTime = new Date();
let _k_resourceEntry = null;

export function findEarliestMonthKey(rows) {
    const allKeys = rows.filter(r => r.Values).flatMap(r => Object.keys(r.Values));

    // keep keys that look like "Nov25", "Dec26", etc.
    const keys = allKeys.filter(k => /^[A-Za-z]{3}\d{2}$/.test(k));

    const dates = keys.map(k => kendo.parseDate(k, 'MMMyy'));

    return new Date(Math.min(...dates));
}

const createGridInternal = (data, startDate, opts = {}) => {
    const $individualCombinedGrid = $('.grid.review-grid');
    _k_resourceEntry?.grid?.destroy();
    $individualCombinedGrid.empty();
    _k_resourceEntry = $individualCombinedGrid
        .kendoResourceEntry({
            columnWidth: 120,
            dataSource: {
                schema: {
                    model: {
                        id: 'scheduleFiveNumber',
                        fields: {
                            scheduleFiveNumber: { editable: false },
                            scheduleFiveType: { editable: false, type: 'string' },
                            item: { editable: false },
                            Values: { editable: true },
                            // Declared here because it is not a column, so the widget's
                            // _getModel cannot derive it from staticColumns. This coerces
                            // the SQL bit to a real boolean. remainingVariance is
                            // deliberately NOT declared here: it IS a column, and
                            // type: 'currency' on it already yields type: 'number'.
                            isVarianceWithinTolerance: { editable: false, type: 'boolean' },
                        },
                    },
                },
                data: data,
            },
            staticColumns: [
                {
                    field: 'projectNumber',
                    type: 'string',
                    title: 'Project Number',
                    width: 110,
                    editable: false,
                    template: function(dataItem) {
                        if (dataItem.projectNumber) {
                            return renderTagList(dataItem.projectNumber, '');
                        }
                    },
                    sortable: true,
                },
                {
                    field: 'projectName',
                    type: 'string',
                    title: 'Project Name',
                    width: 170,
                    editable: false,
                    template: function(dataItem) {
                        if (dataItem.projectName) {
                            return renderTagList(dataItem.projectName, '');
                        }
                    },
                },
                {
                    field: 'status',
                    type: 'string',
                    title: 'Status',
                    width: 85,
                    editable: false,
                    template: function(dataItem) {
                        return renderStatusBadge(dataItem.status);
                    },
                },
                {
                    // Narrowed from 250 to pay for the Variance column. The department
                    // chips are inline-block inside a max-height wrapper, so they stack
                    // and the cell scrolls internally instead of inflating the row.
                    // headerAttributes lets the two-word title wrap: .k-grid th sets
                    // white-space: nowrap, which an inline style overrides.
                    field: 'jobDepartment',
                    type: 'string',
                    title: 'Job Department',
                    width: 120,
                    editable: false,
                    headerAttributes: { style: 'white-space: normal' },
                    template: function(dataItem) {
                        if (dataItem.jobDepartment) {
                            return renderTagList(dataItem.jobDepartment, '', { dedupe: true });
                        }
                    },
                },
                {
                    field: 'projectManagerName',
                    type: 'string',
                    title: 'Project Manager',
                    width: 140,
                    editable: false,
                    headerAttributes: { style: 'white-space: normal' },
                    template: function(dataItem) {
                        if (dataItem.projectManagerName) {
                            return renderTagList(dataItem.projectManagerName, 'tag--blue', { dedupe: true });
                        }
                    },
                },
                {
                    // Total remaining variance across all nine cost types, green when
                    // every cost type is within the +/- $100 allowance and red when any
                    // single one exceeds it. editable: false is required, not cosmetic:
                    // the widget derives firstEditableIndex from the first column whose
                    // editable is undefined, and omitting it here would break tab-to-edit
                    // on the month cells.
                    field: 'remainingVariance',
                    type: 'currency',
                    title: 'Variance',
                    width: 135,
                    editable: false,
                    format: '{0:c2}',
                    attributes: { style: 'text-align: right' },
                    headerAttributes: { style: 'white-space: normal' },
                    template: function(dataItem) {
                        return renderVarianceTag(dataItem);
                    },
                },
                {
                    field: 'backlogRevenue',
                    type: 'currency',
                    title: 'Backlog Revenue',
                    width: 135,
                    editable: false,
                    headerAttributes: { style: 'white-space: normal' },
                },
                {
                    field: 'backlogGp',
                    type: 'currency',
                    title: 'Backlog GP',
                    width: _actualWidth,
                    editable: false,
                    format: '{0:c2}',
                },
            ],
            grid: {
                height: window.innerHeight * 0.75,
                scrollable: { virtual: 'rows' },
                ...buildNoRecordsConfig(opts.suppressNoRecords),
            },
            readonly: true,
            startDate: startDate,
            dataBound(arg) {
                const $rows = $individualCombinedGrid.find('tr.k-master-row');
                $rows.css('cursor', 'pointer');
                $rows.on('click', function() {
                    const uid = $(this).data('uid');
                    const data = _k_resourceEntry.dataSource.data().find(item => item.uid === uid);

                    if (data) {
                        window
                            .open(
                                `${window.location.origin}/Forms/ProjectReview/ScheduleFive?projectInfo=${data.projectNumber}-${data.projectName}`,
                                '_blank'
                            )
                            .focus();
                    }
                });

                applyStatusRowColors();
            },
            sortable: true,
            excel: {
                fileName: `ScheduleFiveOverview_${dateTime.getFullYear()}${dateTime.getMonth() + 1}${dateTime.getDate()}.xlsx`,
                filterable: true,
            },
        })
        .data('kendoResourceEntry');
};

const createGrid = () => {
    const $grid = $('.grid.review-grid');
    const company = $grid.data('company');
    return $.getJSON('/Operations/ScheduleFiveOverview/GetIndividualCombinedScheduleFives', { company: company }).done(data => {
        const startDate = getStartDateForData(data);
        createGridInternal(data, startDate);
    });
};

export const addMaxHeight = output => {
    return `<div style="max-height:${_maxRowHeight}px; overflow-y: auto;">${output}</div>`;
};

export const refreshIndividualGrid = () => {
    _k_resourceEntry.grid.refresh();
};

export const getIndividualGrid = () => _k_resourceEntry?.grid;

export default function() {
    _company = $('.grid.review-grid').data('company');
    setupFilterMultiSelects('js-overview', _company);
    createGridInternal([], new Date(), { suppressNoRecords: true });
    kendo.ui.progress($('.s5-review-wrap'), true);

    Promise.all([createGrid()])
        .then(() => {
            kendo.ui.progress($('.s5-review-wrap'), false);
        })
        .catch(() => {
            kendo.ui.progress($('.s5-review-wrap'), false);
        });

    $(window).resize(() => {
        const { grid } = $('.grid.review-grid').data('kendoResourceEntry');
        grid.resize();
    });
}
